import {Input, Popover} from 'antd';
import {FormInstance} from 'antd/es/form';
import cn from 'classnames';
import {isEqual} from 'lodash-es';
import React, {
    FC, ReactNode, useEffect, memo, useState, CSSProperties, useRef, ChangeEvent, useCallback, useMemo,
} from 'react';

import {
    EEditableInput,
    IEditableTableInputProps,
    TFieldStateType,
    EditableTableInput,
} from 'components/editable-table';
import {IFieldMeta} from 'entities/metadata';
import {TEntity, TEntityValue} from 'modules/data';
import {TStateSetter} from 'shared/types/generics';
import {getPopupContainer} from 'shared/utils';

import s from './editable-cell.module.less';

export const DEFAULT_STATE_CELL = {
    activated: true,
    required: false,
} as TFieldStateType;

const DEFAULT_FIELD_STATE: Record<string, TFieldStateType> = {
    uploadDate: {
        activated: false,
        required: false,
    },
};

type TCellValue =
    | ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    | TEntityValue
    | {value: TEntityValue};

export const getDefaultFieldState = (fieldKey: string) => DEFAULT_FIELD_STATE[fieldKey] || DEFAULT_STATE_CELL;

export interface IEditableCellProps {
    editable?: boolean;
    alwaysEditable?: boolean;
    children?: ReactNode;
    dataIndex: keyof TEntity;
    record: TEntity;
    handleSave: (fieldName: string, value: any) => void;
    key?: string;
    inputType?: EEditableInput;
    inputProps?: IEditableTableInputProps;
    generateInputProps?: (fieldKey: string, record?: TEntity) => IEditableTableInputProps;
    getFieldState?: (fieldKey: string, record: TEntity) => TFieldStateType;
    entityName?: string;
    required?: boolean;
    disabled: boolean;
    placeholder?: string;
    defaultValue?: string & (string | number | readonly string[]);
    field?: IFieldMeta;
    width?: string | number;
    left?: number;
    className?: string;
    format?: string;
    rowId: number;
    formListName: string;
    form?: FormInstance;
    fieldErrors?: string[];
    validating?: boolean;
    onFieldChange?: (fieldName: string, value: any) => void;
    style?: CSSProperties;
    isSelected?: boolean;
    isFocused?: boolean;
    setActiveKey?: TStateSetter<string | null>;
}

export const EditableCell: FC<IEditableCellProps> = memo((props: IEditableCellProps) => {
    const {
        editable = false,
        children,
        dataIndex,
        record = {} as TEntity,
        handleSave,
        inputType,
        inputProps = {},
        alwaysEditable = false,
        generateInputProps,
        entityName,
        getFieldState,
        disabled = false,
        placeholder,
        required = false,
        width,
        field,
        className,
        format,
        rowId,
        form,
        formListName,
        defaultValue,
        fieldErrors = [],
        validating = false,
        onFieldChange,
        left,
        style,
        isFocused,
        setActiveKey,
        ...restProps
    } = props;

    const [editing, setEditing] = useState(alwaysEditable);
    const [localValidating, setLocalValidating] = useState(false);
    const uniqueCellKey = `${rowId}-${dataIndex}`;
    const inputRef = useRef<any>(null);

    const cursorPositionRef = useRef<number | null>(null);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastValueRef = useRef<string>('');

    const stateCell = useMemo(() => getFieldState ? {
        ...getDefaultFieldState(dataIndex as string),
        ...getFieldState(dataIndex, record),
        required,
    } : {
        ...DEFAULT_STATE_CELL,
        required,
    }, [getFieldState, dataIndex, record, required]);

    const fixedCellStyle: CSSProperties | undefined = left !== undefined ? {
        position: 'sticky',
        left: `${left}px`,
    } : undefined;

    const generatedProps = generateInputProps ? generateInputProps(dataIndex as string, record) : {};
    const [value, setValue] = useState(record?.[dataIndex] ?? generatedProps?.defaultValue ?? defaultValue ?? '');
    const InputComponent = inputType ? EditableTableInput[inputType] : Input;

    // Актуализация значения поля
    useEffect(() => {
        if (!editing && record) {
            setValue(record[dataIndex] ?? defaultValue ?? '');
        }
        if (editing && record && value !== record[dataIndex]) setValue(record[dataIndex]);
    }, [record?.[dataIndex], editing, defaultValue]);

    // Управление фокусом
    useEffect(() => {
        if (alwaysEditable && inputRef.current && isFocused) {
            // Восстанавливаем фокус инпута
            inputRef.current.focus();
            // Восстанавливаем позицию курсора
            if (cursorPositionRef.current !== null && inputRef.current.input) {
                const nativeInput = inputRef.current.input || inputRef.current.resizableTextArea?.textArea;
                if (nativeInput && nativeInput.setSelectionRange) {
                    try {
                        nativeInput.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
                    } catch (e) {
                        // Игнорируем ошибки
                    }
                }
            }
        }
    });

    const performSave = useCallback(async (valueToSave: TCellValue) => {
        setLocalValidating(true);
        try {
            form?.setFields([{
                name: [formListName, rowId, dataIndex],
                value: valueToSave,
            }]);
            handleSave(dataIndex as string, valueToSave);
        } finally {
            setLocalValidating(false);
        }
    }, [dataIndex, form, formListName, handleSave, rowId]);

    // Функция вызова сохранения с дебаунсом
    const debouncedSave = (valueToSave: TCellValue) => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            performSave(valueToSave);
            saveTimerRef.current = null;
        }, 300);
    };

    const handleChange = async (newValue: TCellValue) => {
        let valueToSet = newValue;

        if (newValue && typeof newValue === 'object' && 'target' in newValue) {
            const {target} = newValue as ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
            if ('value' in target) {
                valueToSet = target.value;

                // Сохраняем позицию курсора
                if ('selectionStart' in target) {
                    cursorPositionRef.current = target.selectionStart as number + 1;
                }

                if (setActiveKey) {
                    setActiveKey(uniqueCellKey);
                }
            }
        }

        if (Array.isArray(valueToSet)) {
            valueToSet = newValue;
        }
        if (newValue && typeof newValue === 'object' && 'value' in newValue) {
            valueToSet = newValue.value;
        }

        // Сохраняем последнее значение
        lastValueRef.current = valueToSet as string;
        setValue(valueToSet);

        if (onFieldChange) {
            onFieldChange(dataIndex as string, valueToSet);
        }

        if (alwaysEditable) {
            debouncedSave(valueToSet);
        }
    };

    const handleBlur = async () => {
        if (setActiveKey) {
            setActiveKey(null);
        }
        // После потери фокуса сохраняем без дебаунса
        if (alwaysEditable && saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            if (lastValueRef.current !== '') {
                await performSave(lastValueRef.current);
            }
            saveTimerRef.current = null;
        }

        if (editing && !alwaysEditable && record) {
            setLocalValidating(true);
            try {
                form?.setFields([{
                    name: [formListName, rowId, dataIndex],
                    value,
                }]);

                handleSave(dataIndex as string, value);
                setEditing(false);
            } finally {
                setLocalValidating(false);
            }
        }
    };

    const handleFocus = () => {
        if (setActiveKey && !isFocused) {
            setActiveKey(uniqueCellKey);
        }
    };

    const toggleEdit = () => {
        if (!alwaysEditable) {
            setEditing(!editing);
            if (setActiveKey) {
                setActiveKey(uniqueCellKey);
            }
        }
    };

    let childNode = children;

    const isCellEditable = editable && (stateCell.activated || required);
    const isValidationInProgress = localValidating || validating;
    const isDisabled = disabled || stateCell.disabled || !stateCell.activated || isValidationInProgress

    const shouldRenderInput = isCellEditable && (editing || alwaysEditable);

    if (shouldRenderInput) {
        childNode = (
            <Popover
                placement="bottomLeft"
                content={fieldErrors?.join('\n')}
                overlayClassName={cn(
                    s.cell__popover,
                    (field?.isHidden || !fieldErrors?.length) && s.cell__popover_hidden
                )}
                getPopupContainer={getPopupContainer(true)}
            >
                <InputComponent
                    ref={inputRef}
                    className={cn(
                        s.cell__value,
                        (fieldErrors?.length && !isDisabled) && s.cell__input_error
                    )}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    {...inputProps}
                    value={value}
                    entityName={entityName}
                    fixedDropdown={false}
                    isRequired={(required || fieldErrors?.length) && !isDisabled}
                    autoComplete="off"
                    placeholder={placeholder}
                    isFilterable
                    width={width}
                    valueId={record?.id}
                    format={format}
                    autoFocus={!alwaysEditable && !isFocused} // Автофокус только если не в фокусе
                    hidden={field?.isHidden}
                    formFieldKey={dataIndex}
                    defaultValue={defaultValue}
                    {...generatedProps}
                    disabled={isDisabled}
                />
            </Popover>
        );
    } else {
        childNode = (
            <div
                role="presentation"
                className={cn(
                    s.cell__value,
                    field?.isHidden && s.cell__value_hidden,
                )}
                onClick={toggleEdit}
            >
                {children}
            </div>
        );
    }

    return (
        <td
            width={width}
            className={cn(
                s.cell,
                className,
                dataIndex === 'type' && 'type-width-limit',
                fieldErrors.length > 0 && 'editable-cell_error',
            )}
            style={{
                ...style,
                ...fixedCellStyle,
                ...{maxWidth: width},
            }}
            {...restProps}
        >
            {childNode}
        </td>
    );
}, (prevProps, nextProps) => {
    const shouldUpdate =
        prevProps.editable !== nextProps.editable ||
        prevProps.alwaysEditable !== nextProps.alwaysEditable ||
        prevProps.disabled !== nextProps.disabled ||
        prevProps.isSelected !== nextProps.isSelected ||
        prevProps.isFocused !== nextProps.isFocused ||
        prevProps.style !== nextProps.style ||
        // Сравниваем ошибки по содержимому
        !isEqual(prevProps.fieldErrors, nextProps.fieldErrors) ||
        !isEqual(prevProps.record, nextProps.record);

    return !shouldUpdate;
});
